import { radius, spacing, ThemeColors, useColors } from "@/constants/theme";
import { useMemo, useState } from "react";
import { Modal, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

interface OnboardingProps {
  onDone: () => void;
}

interface Slide {
  emoji: string;
  title: string;
  body: string;
}

const SLIDES: Slide[] = [
  {
    emoji: "🔍",
    title: "学びを“レンズ”に変える",
    body: "国語・数学・理科…各教科で学ぶ見方や考え方を「学問レンズカード」として整理。日常を読み解く道具になります。",
  },
  {
    emoji: "✨",
    title: "日常の発見を投稿しよう",
    body: "気づいたことを写真や言葉で記録し、使った学問レンズを紐づけます。テストのための知識を、世界を見る力に。",
  },
  {
    emoji: "📊",
    title: "思考の個性が育つ",
    body: "使ったカードは自動で集計。あなたがよく使う見方・考え方が「思考ポートフォリオ」として可視化され、職業や探究テーマの提案につながります。",
  },
];

export default function Onboarding({ onDone }: OnboardingProps) {
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const [step, setStep] = useState(0);
  const isLast = step === SLIDES.length - 1;
  const slide = SLIDES[step];

  return (
    <Modal visible animationType="fade" transparent onRequestClose={onDone}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Pressable
            accessibilityRole="button"
            onPress={onDone}
            style={styles.skip}
            hitSlop={8}
          >
            <Text style={styles.skipText}>スキップ</Text>
          </Pressable>

          <Text style={styles.emoji}>{slide.emoji}</Text>
          <Text variant="headlineSmall" style={styles.title}>
            {slide.title}
          </Text>
          <Text style={styles.body}>{slide.body}</Text>

          <View style={styles.dots}>
            {SLIDES.map((_, index) => (
              <View
                key={index}
                style={[styles.dot, index === step && styles.dotActive]}
              />
            ))}
          </View>

          <Pressable
            accessibilityRole="button"
            style={styles.button}
            onPress={() => (isLast ? onDone() : setStep((s) => s + 1))}
          >
            <Text style={styles.buttonText}>
              {isLast ? "はじめる" : "次へ"}
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 31, 51, 0.55)",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.xl,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: c.surface,
    borderRadius: radius.xl,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xl,
    alignItems: "center",
  },
  skip: {
    position: "absolute",
    top: spacing.md,
    right: spacing.md,
    padding: spacing.sm,
  },
  skipText: {
    color: c.textMuted,
    fontWeight: "700",
    fontSize: 13,
  },
  emoji: {
    fontSize: 56,
    marginBottom: spacing.md,
  },
  title: {
    fontWeight: "700",
    textAlign: "center",
    color: c.textPrimary,
    marginBottom: spacing.md,
  },
  body: {
    textAlign: "center",
    color: c.textSecondary,
    lineHeight: 22,
    fontSize: 14,
    marginBottom: spacing.xl,
  },
  dots: {
    flexDirection: "row",
    gap: 8,
    marginBottom: spacing.xl,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: c.border,
  },
  dotActive: {
    backgroundColor: c.primary,
    width: 22,
  },
  button: {
    width: "100%",
    minHeight: 50,
    borderRadius: radius.pill,
    backgroundColor: c.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 15,
  },
});
